/*
  volume-nudge.js — interactivity for the Fleet Volume Discount nudge (H-010).
  The <volume-nudge> element is Liquid-rendered inside the cart drawer and
  re-inserted via innerHTML on every cart update, so connectedCallback fires on
  every cart change. All state (count, gap, tier, target line) is precomputed
  server-side and read from data attributes; this file only handles analytics
  and the one-tap Add button.
*/
if (!customElements.get('volume-nudge')) {
  class VolumeNudge extends HTMLElement {
    connectedCallback() {
      const addButton = this.querySelector('[data-volume-nudge-add]');
      if (addButton) addButton.addEventListener('click', this.onAddClick.bind(this));

      const state = this.dataset.state;
      const count = parseInt(this.dataset.count, 10) || 0;

      if (state === 'prominent' || state === 'subtle') {
        // Qty increased (via the drawer stepper) while the prominent banner was
        // showing — counts as a nudge-driven add unless the Add button did it
        // (the button already pushed its own event at click time).
        const prev = window.__volumeNudgePrev;
        if (prev && prev.state === 'prominent' && count > prev.count && !window.__volumeNudgeButtonAdd) {
          this.pushEvent('volume_nudge_add', { method: 'qty_change' });
        }

        // Impression only when the nudge is actually visible (drawer open); the
        // section is server-rendered on every page, so connectedCallback alone
        // would overcount. Deduped per state/count/gap/tier combination.
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const key = [state, this.dataset.count, this.dataset.gap, this.dataset.tier].join(':');
            if (window.__volumeNudgeShownKey !== key) {
              window.__volumeNudgeShownKey = key;
              this.pushEvent('volume_nudge_shown', {});
            }
            this.observer.disconnect();
          });
        });
        this.observer.observe(this);
      }

      window.__volumeNudgeButtonAdd = false;
      window.__volumeNudgePrev = { state, count };
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    pushEvent(eventName, extra) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({
        event: eventName,
        nudge_state: this.dataset.state,
        tracker_count: parseInt(this.dataset.count, 10) || 0,
        tracker_gap: parseInt(this.dataset.gap, 10) || 0,
        next_tier: parseInt(this.dataset.tier, 10) || 0
      }, extra));
    }

    onAddClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      if (button.hasAttribute('aria-busy')) return;

      const line = this.dataset.line;
      const targetQty = parseInt(this.dataset.targetQty, 10);
      const cartItems = (this.closest('cart-drawer') || document).querySelector('cart-items');
      if (!cartItems || !line || !targetQty) return;

      button.setAttribute('aria-busy', 'true');
      window.__volumeNudgeButtonAdd = true;
      this.pushEvent('volume_nudge_add', { method: 'add_button' });

      // Reuse the theme's own cart-change path (same one the qty stepper and
      // remove links use) so Loop subscription lines keep their selling plan
      // and the drawer re-renders through the Section Rendering API.
      cartItems.updateQuantity(line, targetQty);
    }
  }
  customElements.define('volume-nudge', VolumeNudge);
}
